import { atom, computed } from "nanostores";
import { APIRequestError, requestDelete, requestGet, requestPost } from "../utils/request.ts";
import { createRefreshResource, RefreshCancelled } from "../utils/refresh_resource.ts";

interface UserInfoResponse {
    username: string;
    organization_name: string;
    adv_expire: number;
    ai_analysis_enabled?: boolean;
    is_sub?: boolean;
}

/* ==== Stores ==== */
export const $username = atom("");
export const $organizationName = atom("");
export const $advanceExpireAt = atom(0);
export const $aiAnalysisEnabled = atom(false);
export const $isSubAccount = atom(false);
export const $sessionVersion = atom(0);
export const $authChanging = atom(false);
export const getSessionVersion = () => $sessionVersion.get();
const advanceSession = () => $sessionVersion.set(getSessionVersion() + 1);
export const $userInfo = computed([$username, $organizationName, $advanceExpireAt, $aiAnalysisEnabled, $isSubAccount], (username, organization_name, adv_expire, ai_analysis_enabled, is_sub) => ({
    username,
    organization_name,
    adv_expire,
    ai_analysis_enabled,
    is_sub
}));
export const $isLogined = computed($username, (un) => typeof un === "string" && un.length > 0);
export const $now = atom(Date.now() / 1000);
setInterval(() => {
    $now.set(Date.now() / 1000);
}, 60_000);

export const $isAdvanceUser = computed([$advanceExpireAt, $now], (expire, now) => expire >= now);
export const $isAIEnabled = computed($aiAnalysisEnabled, (enabled) => !!enabled);

/* ==== Actions ==== */
export const expireUserInfo = () => {
    advanceSession();
    $username.set("");
    $organizationName.set("");
    $advanceExpireAt.set(0);
    $aiAnalysisEnabled.set(false);
    // $isAdvanceUser is computed, no need to set
    $isSubAccount.set(false);
    try {
        if (typeof globalThis !== "undefined" && (globalThis as any).caches) {
            (globalThis as any).caches.delete("photo_cache_v2").catch(() => {});
            (globalThis as any).caches.delete("photo_cache").catch(() => {});
        }
    } catch {
    }
    console.log("UserInfoExpired");
};
export const updateUserInfo = (infoResp: UserInfoResponse) => {
    if ($username.get() !== (infoResp?.username ?? "")) advanceSession();
    $username.set(infoResp?.username ?? "");
    $organizationName.set(infoResp?.organization_name ?? "");
    $advanceExpireAt.set(infoResp?.adv_expire ?? 0);
    $aiAnalysisEnabled.set(!!infoResp?.ai_analysis_enabled);
    // $isAdvanceUser.set($advanceExpireAt.get() >= (Date.now() / 1000));
    // Fallback: If organization name starts with "Sub" or "Sub-account", assume sub-account
    // This handles cases where backend exception logic might have cleared is_sub flag but kept default org name
    const isSubFallback = (infoResp?.organization_name?.startsWith("Sub") || infoResp?.organization_name?.startsWith("Sub-account"));
    $isSubAccount.set(!!infoResp?.is_sub || isSubFallback);
};

export const login = async (username: string, password: string) => {
    $authChanging.set(true);
    advanceSession();
    const session = getSessionVersion();
    try {
        const resp = await requestPost<UserInfoResponse>("/v2/login", { username, password });
        if (session !== getSessionVersion()) throw new RefreshCancelled();
        updateUserInfo(resp);
    } finally {
        $authChanging.set(false);
    }
};

export const logout = async () => {
    $authChanging.set(true);
    advanceSession();
    try {
        await requestDelete("/v2/login");
        expireUserInfo();
    } finally {
        $authChanging.set(false);
    }
};

const userResource = createRefreshResource(
    signal => requestGet<UserInfoResponse>("/v2/user", {}, signal),
    null as UserInfoResponse | null,
    getSessionVersion,
);
$sessionVersion.listen(() => userResource.reset());

export const refreshUserInfo = async () => {
    if ($authChanging.get()) throw new RefreshCancelled();
    const session = getSessionVersion();
    try {
        const resp = await userResource.refresh();
        if (session !== getSessionVersion()) throw new RefreshCancelled();
        if (resp) updateUserInfo(resp);
        return resp;
    } catch (error) {
        if (session === getSessionVersion() && error instanceof APIRequestError &&
            (error.statusCode === 401 || error.statusCode === 403)) expireUserInfo();
        throw error;
    }
};

/* ==== Events ==== */
void refreshUserInfo().catch(() => {});
